import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useLang } from "../../context/LanguageContext";
import { Trash2, Plus, Upload, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";

const GalleryManager = () => {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [url, setUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const res = await api.get("/gallery");
      setItems(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const addByUrl = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/admin/gallery", { image_url: url.trim() });
      setUrl("");
      toast.success("✓");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setProgress(0);
    let done = 0;
    let failed = 0;
    for (const file of files) {
      if (!file.type.startsWith("image/")) {
        failed++;
        continue;
      }
      const fd = new FormData();
      fd.append("file", file);
      try {
        await api.post("/admin/gallery/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        done++;
      } catch (e) {
        failed++;
        toast.error(`${file.name}: ${formatApiError(e)}`);
      }
      setProgress(Math.round(((done + failed) / files.length) * 100));
    }
    setUploading(false);
    setProgress(0);
    if (done > 0) toast.success(`${done} görsel yüklendi ✓`);
    load();
  };

  const onFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    uploadFiles(files);
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files || []);
    uploadFiles(files);
  };

  const remove = async (id) => {
    if (!window.confirm("?")) return;
    try {
      await api.delete(`/admin/gallery/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="gallery-manager">
      {/* Drop / upload zone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDrop={onDrop}
        className="bg-[#121217] border border-dashed border-[#D4AF37]/40 hover:border-[#D4AF37] transition-colors p-8 text-center"
        data-testid="gallery-drop-zone"
      >
        <Upload size={28} className="text-[#D4AF37] mx-auto mb-3" />
        <div className="text-sm text-white/80">
          Cihazınızdan görsel yükleyin
        </div>
        <div className="text-xs text-white/40 mt-1">
          Sürükle-bırak veya butona tıklayın · JPG, PNG (max 15MB)
        </div>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          data-testid="gallery-upload-button"
          className="mt-5 bg-[#D4AF37] text-[#0A0A0C] hover:bg-[#C5A059] disabled:opacity-50 px-6 py-3 uppercase tracking-[0.2em] text-xs font-bold inline-flex items-center gap-2"
        >
          <Upload size={14} />
          {uploading ? `Yükleniyor… ${progress}%` : "Dosya Seç"}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={onFileChange}
          className="hidden"
          data-testid="gallery-file-input"
        />
        {uploading && (
          <div className="mt-4 h-1 bg-white/10 overflow-hidden">
            <div
              className="h-full bg-[#D4AF37] transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>

      {/* OR — URL input fallback */}
      <div className="flex items-center gap-4">
        <div className="flex-1 h-px bg-white/10" />
        <div className="text-[10px] tracking-[0.4em] uppercase text-white/40">
          veya URL ile
        </div>
        <div className="flex-1 h-px bg-white/10" />
      </div>

      <form
        onSubmit={addByUrl}
        className="bg-[#121217] border border-white/10 p-5 flex flex-col sm:flex-row gap-3 items-stretch sm:items-end"
      >
        <div className="flex-1">
          <label className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]/80 mb-2 block flex items-center gap-2">
            <LinkIcon size={11} /> {t("admin.dashboard.gallery.url_placeholder")}
          </label>
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            data-testid="gallery-url-input"
            placeholder="https://..."
            className="w-full bg-transparent border-b border-white/20 rounded-none px-0 py-2 focus:border-[#D4AF37] focus:outline-none text-white text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          data-testid="gallery-add"
          className="border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 disabled:opacity-50 px-6 py-3 uppercase tracking-[0.2em] text-xs font-bold flex items-center gap-2"
        >
          <Plus size={14} /> {t("admin.dashboard.gallery.add_btn")}
        </button>
      </form>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.map((g) => (
          <div
            key={g.id}
            className="relative group aspect-square overflow-hidden bg-[#121217] border border-white/10"
            data-testid={`gallery-tile-${g.id}`}
          >
            <img src={g.image_url} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-[#0A0A0C]/0 group-hover:bg-[#0A0A0C]/70 transition-colors duration-300 flex items-center justify-center">
              <button
                onClick={() => remove(g.id)}
                data-testid={`gallery-delete-${g.id}`}
                className="opacity-0 group-hover:opacity-100 transition-opacity border border-red-400/50 text-red-400 hover:bg-red-900/30 px-4 py-2 text-xs flex items-center gap-2"
              >
                <Trash2 size={12} /> {t("admin.dashboard.gallery.delete")}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GalleryManager;
